<!-- Chips for one run's error categories; `none` when it came back clean. -->
<ng-template #errorChips let-entries>
    <div class="chips">
        @for (entry of entries; track entry.key) {
            <span class="chip">{{ entry.key }} ×{{ entry.value }}</span>
        } @empty {
            <span class="chip chip-clean">none</span>
        }
    </div>
</ng-template>

<div class="crawler-mon">
    <!-- Toolbar -->
    <div class="toolbar">
        <div class="toolbar-left">
            <h2 class="page-title">Crawler Monitor</h2>
            <span class="page-subtitle"
                >lnwstore nightly pipeline — product → sales → order, 02:00
                ICT</span
            >
        </div>
        <div class="toolbar-right">
            @if (lastRefreshed) {
                <span class="refreshed">Refreshed {{ lastRefreshed }}</span>
            }
            <div class="toolbar-field">
                <span class="toolbar-label">Runs</span>
                <dx-select-box
                    [items]="runLimitOptions"
                    [(value)]="runLimit"
                    [width]="90"
                    [disabled]="isLoading"
                    (onValueChanged)="onRunLimitChanged()"
                ></dx-select-box>
            </div>
            <div class="toolbar-field">
                <span class="toolbar-label">Auto</span>
                <dx-switch
                    [(value)]="autoRefresh"
                    (onValueChanged)="onAutoRefreshChanged()"
                ></dx-switch>
            </div>
            <dx-button
                stylingMode="contained"
                type="default"
                icon="refresh"
                text="Refresh"
                [disabled]="isLoading"
                (onClick)="load()"
            ></dx-button>
        </div>
    </div>

    <!-- Overall verdict -->
    <div class="banner" [class]="'level-' + overall.level">
        <span class="banner-icon">{{ overall.icon }}</span>
        <div class="banner-body">
            <div class="banner-title">{{ overall.title }}</div>
            <div class="banner-detail">{{ overall.detail }}</div>
        </div>
    </div>

    <!-- Per-crawler cards -->
    <div class="cards">
        @for (card of cards; track card.crawler) {
            <div class="card" [class]="'level-' + card.level">
                <div class="card-head">
                    <span class="card-icon">{{ card.icon }}</span>
                    <div class="card-name">
                        <div class="card-title">{{ card.crawler }}</div>
                        <div class="card-desc">{{ card.description }}</div>
                    </div>
                    <span class="card-status">{{ card.statusText }}</span>
                </div>

                <div class="card-headline">{{ card.headline }}</div>

                @if (card.lastRun; as lastRun) {
                    <!-- The crawler's own Thai verdict, verbatim -->
                    <div class="card-reason">“{{ lastRun.reason }}”</div>
                }

                <div class="card-grid">
                    <div class="cell">
                        <span class="cell-label">Last run</span>
                        <span class="cell-value">{{ card.lastRunAgo }}</span>
                        <span class="cell-sub">{{ card.lastRunAt }}</span>
                    </div>
                    <div class="cell">
                        <span class="cell-label">Duration</span>
                        <span class="cell-value">{{ card.durationText }}</span>
                        <span class="cell-sub">{{ card.versionText }}</span>
                    </div>
                    <div class="cell">
                        <span class="cell-label">Last clean run</span>
                        <span class="cell-value">{{ card.lastOkRunAgo }}</span>
                        @if (card.completedThrough) {
                            <span class="cell-sub"
                                >data through {{ card.completedThrough }}</span
                            >
                        }
                    </div>
                </div>

                <!-- Volume drift: catches breakage that still reports "ok" -->
                @if (card.trend.length > 1) {
                    <div class="trend">
                        <div class="trend-head">
                            <span class="cell-label">{{
                                card.primaryMetricLabel
                            }}</span>
                            <span
                                class="trend-value"
                                [class.drop]="card.volumeDrop"
                                >{{ card.latestValue | formatNumber }}</span
                            >
                        </div>
                        <dx-sparkline
                            class="sparkline"
                            [dataSource]="card.trend"
                            argumentField="label"
                            valueField="value"
                            type="line"
                            [showMinMax]="true"
                            [lineColor]="
                                card.volumeDrop ? '#ff5252' : '#4aa3ff'
                            "
                        ></dx-sparkline>
                        <div class="trend-foot" [class.drop]="card.volumeDrop">
                            {{ card.deltaText }}
                        </div>
                    </div>
                }

                @if (card.lastRun) {
                    <div class="card-errors">
                        <span class="cell-label">Errors in last run</span>
                        <ng-container
                            *ngTemplateOutlet="
                                errorChips;
                                context: { $implicit: card.errorChips }
                            "
                        ></ng-container>
                    </div>
                }
            </div>
        }
    </div>

    <!-- Diagnosis: what broke and what to do about it -->
    <div class="section">
        <h3 class="section-title">
            Diagnosis
            <span class="section-sub"
                >error categories seen across the loaded runs — “active” means
                it hit the most recent run</span
            >
        </h3>

        @for (issue of issues; track issue.crawler + ":" + issue.category) {
            <div
                class="issue"
                [class]="'kind-' + issue.kind"
                [class.inactive]="!issue.active"
            >
                <div class="issue-head">
                    <span class="issue-crawler">{{ issue.crawler }}</span>
                    <span class="issue-category">{{ issue.category }}</span>
                    <span class="issue-kind">{{ issue.kindText }}</span>
                    @if (issue.active) {
                        <span class="issue-active">active</span>
                    }
                    <span class="issue-counts"
                        >{{ issue.total }} occurrence{{
                            issue.total > 1 ? "s" : ""
                        }}
                        across {{ issue.runCount }} run{{
                            issue.runCount > 1 ? "s" : ""
                        }}
                        · last {{ issue.lastSeenAgo }}</span
                    >
                </div>
                <div class="issue-meaning">{{ issue.meaning }}</div>
                <div class="issue-action"><b>Fix:</b> {{ issue.action }}</div>
                @if (issue.sampleMessages.length > 0) {
                    <div class="issue-samples">
                        @for (message of issue.sampleMessages; track $index) {
                            <pre>{{ message }}</pre>
                        }
                    </div>
                }
            </div>
        } @empty {
            <div class="empty">
                No errors recorded in the loaded window. Nothing to fix.
            </div>
        }
    </div>

    <!-- Full run history -->
    <div class="section">
        <h3 class="section-title">
            Run history
            <span class="section-sub"
                >expand a row for counts, metrics and raw error samples</span
            >
        </h3>
        <dx-data-grid
            [dataSource]="runs"
            keyExpr="_id"
            [showBorders]="true"
            [columnAutoWidth]="true"
            [wordWrapEnabled]="true"
            [hoverStateEnabled]="true"
            height="520"
        >
            <dxo-scrolling mode="virtual"></dxo-scrolling>
            <dxo-search-panel [visible]="true" [width]="240"></dxo-search-panel>
            <dxo-filter-row [visible]="true"></dxo-filter-row>
            <dxo-header-filter [visible]="true"></dxo-header-filter>
            <dxo-paging [pageSize]="25"></dxo-paging>
            <dxo-pager
                [visible]="true"
                [showPageSizeSelector]="true"
                [allowedPageSizes]="[10, 25, 50]"
                [showInfo]="true"
            ></dxo-pager>

            <dxi-column
                dataField="startedAt"
                caption="Started"
                dataType="datetime"
                format="d MMM yyyy HH:mm"
                sortOrder="desc"
                [width]="170"
            ></dxi-column>
            <dxi-column
                dataField="crawler"
                caption="Crawler"
                [width]="100"
            ></dxi-column>
            <dxi-column
                dataField="status"
                caption="Status"
                [width]="110"
                cellTemplate="statusCell"
            ></dxi-column>
            <dxi-column
                dataField="durationMs"
                caption="Duration"
                [width]="110"
                cellTemplate="durationCell"
            ></dxi-column>
            <dxi-column
                dataField="reason"
                caption="Reason (from crawler)"
                [minWidth]="260"
            ></dxi-column>
            <dxi-column
                dataField="version"
                caption="Version"
                [width]="140"
            ></dxi-column>

            <div *dxTemplate="let cell of 'statusCell'">
                <span [class]="'status-pill status-' + cell.data.status">{{
                    cell.data.status
                }}</span>
            </div>
            <div *dxTemplate="let cell of 'durationCell'">
                {{ humanDuration(cell.data.durationMs) }}
            </div>

            <dxo-master-detail
                [enabled]="true"
                template="runDetail"
            ></dxo-master-detail>
            <div *dxTemplate="let row of 'runDetail'">
                @let detail = runDetail(row.data);
                <div class="detail">
                    <div class="detail-block">
                        <div class="detail-title">Counts</div>
                        @for (entry of detail.counts; track entry.key) {
                            <div class="kv">
                                <span class="k">{{ entry.key }}</span>
                                <span class="v">{{ entry.value }}</span>
                            </div>
                        } @empty {
                            <div class="empty-inline">none</div>
                        }
                    </div>
                    <div class="detail-block">
                        <div class="detail-title">Metrics</div>
                        @for (entry of detail.metrics; track entry.key) {
                            <div class="kv">
                                <span class="k">{{ entry.key }}</span>
                                <span class="v">{{ entry.value }}</span>
                            </div>
                        } @empty {
                            <div class="empty-inline">none</div>
                        }
                    </div>
                    <div class="detail-block detail-wide">
                        <div class="detail-title">Errors</div>
                        <ng-container
                            *ngTemplateOutlet="
                                errorChips;
                                context: { $implicit: detail.errors }
                            "
                        ></ng-container>
                        <div class="samples">
                            @for (sample of detail.samples; track $index) {
                                <div class="sample">
                                    <span class="sample-cat">{{
                                        sample.category
                                    }}</span>
                                    <pre>{{ sample.message }}</pre>
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </dx-data-grid>
    </div>

    <dx-load-panel
        [visible]="isLoading"
        [showIndicator]="true"
        [showPane]="true"
        [shading]="false"
        message="Reading crawl_runs…"
    ></dx-load-panel>
</div>
